import { currentUser, pb } from "@/lib/pb";
import type { FlowCategoriesResponse } from "@/lib/pb-types";
import type { FlowCard, FlowCitation, FlowComment, FlowDecision, FlowVersion, SharedOwner } from "./model";

/**
 * Reads go straight to the collections. Every write but archiving a category
 * goes through the lifecycle routes in `pb_hooks/flows-lib.js`, because the
 * collections have null write rules.
 *
 * The rules also let an org member read the other members' cutter work (categories a Worker
 * picks, `flow_categories.shared`, migration 1785801659), so every list of the account's own
 * cards and categories names its owner; the shared ones are read on their own below.
 */

const post = <T>(path: string, body: unknown) => pb.send<T>(path, { method: "POST", body });

/** Enough for any real backlog; done cards beyond this stop being worth a list. */
const MAX_LISTED_CARDS = 500;
const CARD_FIELDS =
  "id,category,title,summary,kind,status,decision,version,agent_label,picked_by,picked_at,decided_at,done_at,done_note,origin,question,question_at,source_kind,source_id,source_title,replaces,replaced_by,created,updated";

/** `user = <the signed-in account>`, with `rest` after it: what keeps a list to the account's own rows. */
export function ownFilter(rest = ""): string {
  const own = pb.filter("user = {:user}", { user: currentUser()?.id ?? "" });
  return rest ? `${own} && ${rest}` : own;
}

export function fetchCategories(signal?: AbortSignal) {
  return pb.collection("flow_categories").getFullList<FlowCategoriesResponse>({ requestKey: null, signal, sort: "name", filter: ownFilter() });
}

export async function fetchCards(signal?: AbortSignal): Promise<FlowCard[]> {
  const page = await pb.collection("flow_cards").getList<FlowCard>(1, MAX_LISTED_CARDS, {
    requestKey: null,
    signal,
    sort: "-updated",
    filter: ownFilter(),
    fields: CARD_FIELDS,
    skipTotal: true,
  });
  return page.items;
}

/** The badge and the chime: the account's own cards waiting for it, never another member's. */
export async function fetchReviewCount(signal?: AbortSignal): Promise<number> {
  const page = await pb.collection("flow_cards").getList(1, 1, {
    requestKey: null,
    signal,
    filter: ownFilter("status = 'review' && category.archived_at = ''"),
    fields: "id",
  });
  return page.totalItems;
}

/** Other members' cutter categories (the rules return only shared ones of org members). */
export function fetchSharedCategories(signal?: AbortSignal) {
  return pb.collection("flow_categories").getFullList<FlowCategoriesResponse>({
    requestKey: null,
    signal,
    sort: "name",
    filter: pb.filter("user != {:user} && shared = true", { user: currentUser()?.id ?? "" }),
  });
}

/** Other members' cards in those categories, read-only here, with `user` to tell whose each is. */
export async function fetchSharedCards(signal?: AbortSignal): Promise<FlowCard[]> {
  const page = await pb.collection("flow_cards").getList<FlowCard>(1, MAX_LISTED_CARDS, {
    requestKey: null,
    signal,
    sort: "-updated",
    filter: pb.filter("user != {:user} && category.shared = true", { user: currentUser()?.id ?? "" }),
    fields: `${CARD_FIELDS},user`,
    skipTotal: true,
  });
  return page.items;
}

/** Their names (the `users` rules show each account only itself): flows-lib.js sharedOwners. */
export async function fetchSharedOwners(signal?: AbortSignal): Promise<SharedOwner[]> {
  const result = await pb.send<{ owners: SharedOwner[] }>("/api/flows/shared-owners", { method: "GET", requestKey: null, signal });
  return result.owners ?? [];
}

export function fetchVersions(card: string, signal?: AbortSignal) {
  return pb.collection("flow_versions").getFullList<FlowVersion>({
    requestKey: null,
    signal,
    filter: pb.filter("card = {:card}", { card }),
    sort: "number",
  });
}

export function fetchComments(card: string, signal?: AbortSignal) {
  return pb.collection("flow_comments").getFullList<FlowComment>({
    requestKey: null,
    signal,
    filter: pb.filter("card = {:card}", { card }),
    sort: "created",
  });
}

export type DecideInput = { card: string; version: number; decision: FlowDecision; comment: string; citations: FlowCitation[] };

export const decideCard = (input: DecideInput) => post<FlowCard>("/api/flows/cards/decide", input);
export const reopenCard = (card: string) => post<FlowCard>("/api/flows/cards/reopen", { card });
export const releaseCard = (card: string) => post<FlowCard>("/api/flows/cards/release", { card });
/** Pipe stops a card nobody needs any more: done with his reason; its source goes back unapproved (flows-lib.js close). */
export const closeCard = ({ card, reason }: { card: string; reason: string }) => post<FlowCard>("/api/flows/cards/close", { card, reason });
export const answerCard = ({ card, body }: { card: string; body: string }) => post<FlowCard>("/api/flows/cards/answer", { card, body });

/** Where queued work comes from. The description is shown so Pipe knows what the agent will read. */
export type QueueSource = {
  kind: "inbox" | "task";
  id: string;
  title: string;
  description: string;
  sender?: string;
  date?: string;
};

export type QueueCardInput = {
  source: QueueSource;
  /** A category key or name; the server creates the category when it is new. */
  category: string;
  title: string;
  instructions: string;
  images?: readonly File[];
};

/**
 * Hands a Task or an Inbox item to the agents as a queued card whose v1 is the
 * brief. JSON unless there are images, which only travel as multipart.
 */
export function queueCard(input: QueueCardInput): Promise<FlowCard> {
  const fields = {
    source_kind: input.source.kind,
    source_id: input.source.id,
    category: input.category,
    title: input.title,
    instructions: input.instructions,
  };
  if (!input.images?.length) return post<FlowCard>("/api/flows/cards/queue", fields);
  const body = new FormData();
  for (const [name, value] of Object.entries(fields)) body.set(name, value);
  for (const image of input.images) body.append("images", image, image.name);
  return post<FlowCard>("/api/flows/cards/queue", body);
}

/** Takes queued work back before any agent picked it; the source returns where it was. */
export const unqueueCard = (card: string) => post<{ restored: boolean; source: unknown }>("/api/flows/cards/unqueue", { card });

export function setCategoryArchived(id: string, archived: boolean) {
  return pb.collection("flow_categories").update(id, { archived_at: archived ? new Date().toISOString() : "" });
}
